/**
 * @fileoverview
 * WebSocket Service - Manages WebSocket connections
 * 
 * @version 1.0.0
 * @author KATALYST TEAM
 */

import { Injectable, inject, signal } from '@angular/core';
import { Observable, Subject, fromEvent, merge } from 'rxjs';
import { map, filter } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthService } from '../authentication/services';
import { NotificationService } from './notification.service';

export interface WebSocketMessage {
  type: string;
  data: any;
  timestamp: Date;
}

export enum WebSocketEvent {
  NOTIFICATION = 'notification',
  AUDIT_UPDATE = 'audit_update',
  USER_UPDATE = 'user_update',
  SYSTEM_MESSAGE = 'system_message',
}

@Injectable({
  providedIn: 'root'
})
export class WebSocketService {
  private authService = inject(AuthService);
  private notificationService = inject(NotificationService);

  private socket: WebSocket | null = null;
  private messageSubject = new Subject<WebSocketMessage>();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 3000;

  // State signals
  private connectedSignal = signal<boolean>(false);
  private reconnectingSignal = signal<boolean>(false);

  // Public readonly signals
  readonly connected = this.connectedSignal.asReadonly();
  readonly reconnecting = this.reconnectingSignal.asReadonly();

  /**
   * Connect to WebSocket server
   */
  connect(): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      console.log('[WebSocket] Already connected');
      return;
    }

    const token = this.authService.user()?.id; // Or use actual token
    const wsUrl = `${environment.wsUrl}?token=${token}`;

    try {
      this.socket = new WebSocket(wsUrl);

      this.socket.onopen = () => {
        console.log('[WebSocket] Connected');
        this.connectedSignal.set(true);
        this.reconnectingSignal.set(false);
        this.reconnectAttempts = 0;
      };

      this.socket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          this.handleMessage(message);
        } catch (error) {
          console.error('[WebSocket] Failed to parse message', error);
        }
      };

      this.socket.onerror = (error) => {
        console.error('[WebSocket] Error', error);
        this.connectedSignal.set(false);
      };

      this.socket.onclose = () => {
        console.log('[WebSocket] Disconnected');
        this.connectedSignal.set(false);
        this.attemptReconnect();
      };
    } catch (error) {
      console.error('[WebSocket] Connection failed', error);
      this.attemptReconnect();
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
      this.connectedSignal.set(false);
    }
  }

  /**
   * Send message to server
   */
  send(type: string, data: any): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      const message = JSON.stringify({ type, data, timestamp: new Date() });
      this.socket.send(message);
    } else {
      console.warn('[WebSocket] Cannot send message, not connected');
    }
  }

  /**
   * Subscribe to messages of specific type
   */
  on(type: string): Observable<any> {
    return this.messageSubject.pipe(
      filter(message => message.type === type),
      map(message => message.data)
    );
  }

  /**
   * Subscribe to all messages
   */
  onAll(): Observable<WebSocketMessage> {
    return this.messageSubject.asObservable();
  }

  /**
   * Handle incoming message
   */
  private handleMessage(message: WebSocketMessage): void {
    this.messageSubject.next(message);

    // Handle specific message types
    switch (message.type) {
      case WebSocketEvent.NOTIFICATION:
        this.handleNotificationMessage(message.data);
        break;
      case WebSocketEvent.AUDIT_UPDATE:
        // Emit to subscribers
        break;
      case WebSocketEvent.USER_UPDATE:
        // Emit to subscribers
        break;
      case WebSocketEvent.SYSTEM_MESSAGE:
        this.notificationService.info(message.data.message, 'System Message');
        break;
    }
  }

  /**
   * Handle notification messages
   */
  private handleNotificationMessage(data: any): void {
    const { type, title, message } = data;
    
    switch (type) {
      case 'success':
        this.notificationService.success(message, title);
        break;
      case 'error':
        this.notificationService.error(message, title);
        break;
      case 'warning':
        this.notificationService.warning(message, title);
        break;
      default:
        this.notificationService.info(message, title);
    }
  }

  /**
   * Attempt to reconnect
   */
  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('[WebSocket] Max reconnect attempts reached');
      this.reconnectingSignal.set(false);
      return;
    }

    this.reconnectAttempts++;
    this.reconnectingSignal.set(true);

    console.log(`[WebSocket] Reconnecting... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    setTimeout(() => {
      this.connect();
    }, this.reconnectDelay * this.reconnectAttempts);
  }
}

